import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Edit, Eye, Plus, Loader, History } from 'lucide-react';
import { useRoadmapData } from '../hooks/useRoadmapData';
import { Category, Task, TimePeriod } from '../types/roadmap';
import { RoadmapGrid } from './roadmap/RoadmapGrid';
import { TaskCard } from './roadmap/TaskCard';
import { CategoryForm } from './roadmap/CategoryForm';
import { TaskForm } from './roadmap/TaskForm';

const PIAlignmentEditor: React.FC = () => {
  const {
    categories,
    tasks,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    createTask,
    updateTask,
    deleteTask,
    moveTask,
  } = useRoadmapData();

  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormDefaults, setTaskFormDefaults] = useState<{ categoryId?: string; timePeriod?: TimePeriod }>({});
  const [showHistorical, setShowHistorical] = useState(false);
  const [showFuture, setShowFuture] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id);
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over || active.id === over.id) return;

    const task = tasks.find(t => t.id === active.id);
    if (!task) return;

    // Parse the droppable ID: format is "category_id|||time_period"
    const overId = over.id.toString();
    const parts = overId.split('|||');
    const targetCategoryId = parts[0];
    const targetTimePeriod = parts[1];

    if (!targetCategoryId || !targetTimePeriod) return;

    // Get tasks in the target column
    const targetTasks = tasks.filter(
      t => t.category_id === targetCategoryId && t.time_period === targetTimePeriod
    );

    // Calculate new display_order (place at end of column)
    const maxOrder = targetTasks.length > 0
      ? Math.max(...targetTasks.map(t => t.display_order))
      : -1;

    try {
      await moveTask(task.id, {
        category_id: targetCategoryId,
        time_period: targetTimePeriod as TimePeriod,
        display_order: maxOrder + 1,
      });
    } catch (error) {
      console.error('Failed to move task:', error);
    }
  };

  const handleAddTask = (categoryId: string, timePeriod: TimePeriod) => {
    setTaskFormDefaults({ categoryId, timePeriod });
    setShowTaskForm(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setShowTaskForm(true);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await deleteTask(taskId);
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category? All tasks in this category will also be deleted.')) {
      try {
        await deleteCategory(categoryId);
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  };

  const closeAllForms = () => {
    setShowCategoryForm(false);
    setShowTaskForm(false);
    setEditingCategory(null);
    setEditingTask(null);
    setTaskFormDefaults({});
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="text-center text-white">
          <Loader className="animate-spin mx-auto mb-4" size={48} />
          <p>Loading roadmap data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="bg-red-600 text-white p-6 rounded-lg max-w-lg">
          <h2 className="text-xl font-bold mb-2">Error Loading Roadmap</h2>
          <p className="mb-4">{error}</p>
          <p className="text-sm opacity-90">
            Make sure the backend API server is running:
            <code className="block mt-2 bg-red-700 p-2 rounded">
              cd backend && ./start_server.sh
            </code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-800 to-gray-900 p-4">
      <div className="w-full">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="flex justify-between items-center mb-2">
            <Link
              to="/roadmap"
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-semibold"
            >
              <Eye size={18} />
              Read-Only View
            </Link>

            <div className="flex-1 text-center">
              <h1 className="text-3xl font-bold text-white mb-1">
                <Edit className="inline mr-2" size={28} />
                PI Roadmap Editor
              </h1>
              <p className="text-gray-300 text-sm">Drag tasks to move between periods | Click to edit</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowCategoryForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-semibold"
              >
                <Plus size={18} />
                Add Category
              </button>
            </div>
          </div>

          {/* Show Historical and Future Checkboxes */}
          <div className="flex justify-center gap-4 mb-3">
            <label className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 rounded-lg cursor-pointer hover:bg-opacity-20 transition-all">
              <input
                type="checkbox"
                checked={showHistorical}
                onChange={(e) => setShowHistorical(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <History size={16} className="text-gray-300" />
              <span className="text-sm font-medium text-gray-300">Show Historical Periods (PI5)</span>
            </label>
            <label className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 rounded-lg cursor-pointer hover:bg-opacity-20 transition-all">
              <input
                type="checkbox"
                checked={showFuture}
                onChange={(e) => setShowFuture(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-300">Show Future PIs (PI7+)</span>
            </label>
          </div>
        </div>

        {/* Roadmap Grid */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <RoadmapGrid
            categories={categories}
            tasks={tasks}
            showHistorical={showHistorical}
            showFuture={showFuture}
            onAddTask={handleAddTask}
            onEditTask={handleEditTask}
            onDeleteTask={handleDeleteTask}
            onEditCategory={handleEditCategory}
            onDeleteCategory={handleDeleteCategory}
          />

          <DragOverlay>
            {activeTask ? (
              <TaskCard task={activeTask} isDragging />
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Category Form Modal */}
        {showCategoryForm && (
          <CategoryForm
            category={editingCategory}
            categories={categories}
            onSave={async (data) => {
              try {
                if (editingCategory) {
                  await updateCategory(editingCategory.id, data as import('../types/roadmap').CategoryUpdate);
                } else {
                  await createCategory(data as import('../types/roadmap').CategoryCreate);
                }
                closeAllForms();
              } catch (error) {
                console.error('Failed to save category:', error);
              }
            }}
            onCancel={closeAllForms}
          />
        )}

        {/* Task Form Modal */}
        {showTaskForm && (
          <TaskForm
            task={editingTask}
            categories={categories}
            defaults={taskFormDefaults}
            onSave={async (data) => {
              try {
                if (editingTask) {
                  await updateTask(editingTask.id, data as import('../types/roadmap').TaskUpdate);
                } else {
                  await createTask(data as import('../types/roadmap').TaskCreate);
                }
                closeAllForms();
              } catch (error) {
                console.error('Failed to save task:', error);
              }
            }}
            onCancel={closeAllForms}
          />
        )}
      </div>
    </div>
  );
};

export default PIAlignmentEditor;
